/**
 * ストレージ抽象層。
 *
 * Web では `window.localStorage`、Capacitor ネイティブ（Android/iOS）では
 * `@capacitor/preferences` を使う。API は常に async で統一する
 * （Preferences が async しか提供しないため）。
 *
 * 既存の同期 API（`loadSettings()` 等）との互換性を保つため、ネイティブでは
 * 起動時にいちど `hydrate()` で Preferences → `window.localStorage` にコピー
 * しておき、以降は `window.localStorage` を同期読み書きする「ミラー戦略」を採る。
 *
 * 書き込み時は localStorage に即時反映 + Preferences にも非同期で永続化。
 * これにより UI コードは従来通り同期で扱え、プロセス再起動後も設定が残る。
 *
 * ---
 * 参考ルール:
 *   - `.claude/rules/no-silent-fallback.md`
 *   - `.claude/rules/no-hardcode.md`
 *
 * Phase 4 以降の注意:
 *   - 認証トークンなど機微情報を本モジュールで扱う場合は、localStorage への
 *     ミラーを禁止し、Preferences 直読み書きの async API を別途用意すること。
 *     （本ラッパはあくまで「設定値」向け）。
 */
import { isCapacitorNative } from '../utils/platform'

/** ミラー済みキー（別領域の hydrate が互いを潰さないようにキー単位で管理する）。 */
const hydratedKeys = new Set<string>()

/**
 * 起動時にネイティブの Preferences から localStorage にデータを引き上げる。
 *
 * Web 環境では何もしない（localStorage が一次ストレージ）。
 * ネイティブ環境で Preferences にキーが存在する場合のみ localStorage に上書き。
 *
 * 返り値: ネイティブで実際にミラー処理を行ったかどうか（デバッグ用）。
 */
export async function hydrateStorage(keys: readonly string[]): Promise<boolean> {
  if (!isCapacitorNative()) return false

  const pendingKeys = keys.filter((key) => !hydratedKeys.has(key))
  if (pendingKeys.length === 0) return false

  try {
    const { Preferences } = await import('@capacitor/preferences')
    for (const key of pendingKeys) {
      const { value } = await Preferences.get({ key })
      if (value != null) {
        try {
          window.localStorage.setItem(key, value)
        } catch (err) {
          // localStorage が使えない端末はほぼ無いが、念のためログ。
          // fallback:ok — mirror 失敗時は Preferences 側の読み書きに frais rebuild される。
          console.warn('[storage] localStorage mirror failed:', err)
        }
      }
      hydratedKeys.add(key)
    }
    return true
  } catch (err) {
    // @capacitor/preferences が解決できない（未インストール等）は設計上の異常。
    // サイレントに潰さず、警告ログのみ残して localStorage のみで継続する。
    console.warn('[storage] Preferences hydrate failed:', err)
    return false
  }
}

/**
 * 値を同期的に読み込む（localStorage ミラーから）。
 *
 * ネイティブ環境で hydrate 前に呼ばれた場合は null を返す可能性がある。
 * 設定画面などは `hydrateStorage()` を起動時に必ず呼ぶ前提。
 */
export function readStringSync(key: string): string | null {
  try {
    return window.localStorage.getItem(key)
  } catch {
    // fallback:ok — localStorage アクセス不可（SSR / プライベートブラウジング）。
    return null
  }
}

/**
 * 値を永続化する。localStorage には同期で書き、ネイティブでは
 * Preferences にも非同期でミラー書き込みする。
 */
export function writeString(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value)
  } catch (err) {
    // localStorage 書き込み失敗は呼び出し元に伝える必要がある（UI で通知する等）。
    throw err
  }

  if (isCapacitorNative()) {
    // ネイティブ環境のみ Preferences にもミラー。await しないが、失敗時は
    // 警告ログを残す（エラーは localStorage 書き込み成功時点で握り潰さない＝実害なし）。
    void (async () => {
      try {
        const { Preferences } = await import('@capacitor/preferences')
        await Preferences.set({ key, value })
      } catch (err) {
        console.warn('[storage] Preferences mirror write failed:', err)
      }
    })()
  }
}

/** テスト用: 内部の hydrate フラグをリセット。 */
export function _resetHydratedForTest(): void {
  hydratedKeys.clear()
}
